import type {
  ConnectorConnectionListApiResponse,
  ConnectorConnectionListResponse,
  ConnectorProvider,
  ConnectorProviderListApiResponse,
  OAuthCallbackApiResponse,
  OAuthCallbackRequest,
  OAuthCallbackResponse,
  OAuthInitiateApiResponse,
  OAuthInitiateRequest,
  OAuthInitiateResponse,
} from '../interfaces/connector.interface'
import {
  transformConnectorConnectionListFromApi,
  transformConnectorProviderFromApi,
  transformOAuthCallbackFromApi,
  transformOAuthInitiateFromApi,
} from '../interfaces/connector-transform.util'
import { agentsApiSlice } from './agents-api.store'

const API_BASE = '/api/v1/connectors'

const connectorEndpoints = agentsApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getConnectorProviders: builder.query<ConnectorProvider[], void>({
      query: () => `${API_BASE}/providers`,
      transformResponse: (response: ConnectorProviderListApiResponse) =>
        response.items.map(transformConnectorProviderFromApi),
      providesTags: ['Connectors'],
    }),

    getConnectorConnections: builder.query<ConnectorConnectionListResponse, void>({
      query: () => `${API_BASE}/connections`,
      transformResponse: (response: ConnectorConnectionListApiResponse) =>
        transformConnectorConnectionListFromApi(response),
      providesTags: ['Connectors'],
    }),

    initiateOAuth: builder.mutation<OAuthInitiateResponse, OAuthInitiateRequest>({
      query: ({ provider, redirectUri }) => ({
        url: `${API_BASE}/oauth-initiate`,
        method: 'POST',
        body: { provider, redirect_uri: redirectUri },
      }),
      transformResponse: (response: OAuthInitiateApiResponse) =>
        transformOAuthInitiateFromApi(response),
    }),

    completeOAuthCallback: builder.mutation<OAuthCallbackResponse, OAuthCallbackRequest>({
      query: (body) => ({
        url: `${API_BASE}/oauth-callback`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: OAuthCallbackApiResponse) =>
        transformOAuthCallbackFromApi(response),
      invalidatesTags: ['Connectors'],
    }),

    revokeConnection: builder.mutation<void, string>({
      query: (connectionId) => ({
        url: `${API_BASE}/connections/${connectionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Connectors'],
    }),
  }),
})

export const {
  useGetConnectorProvidersQuery,
  useGetConnectorConnectionsQuery,
  useInitiateOAuthMutation,
  useCompleteOAuthCallbackMutation,
  useRevokeConnectionMutation,
} = connectorEndpoints
